import app from 'flarum/forum/app';
import { extend } from 'flarum/common/extend';
import UserCard from 'flarum/forum/components/UserCard';
/**
 * The profile header, in the shape a forum member expects it.
 *
 * Flarum's card says who somebody is; an IPS profile also says at a glance how
 * much they have done here — posts, discussions, solutions — because that is
 * what tells you whether the person answering your question knows the place.
 * Those numbers exist on the user already (`commentCount`, `discussionCount`,
 * `bestAnswerCount`); the only reason they are not on the card is that nothing
 * puts them there. Flarum keeps them in the sidebar nav, where they read as
 * navigation rather than as a member's standing.
 *
 * They go through `infoItems()` rather than a new row so they sit in the list
 * the card already renders, and the stylesheet turns that list into the
 * separated stat strip. One hook, and no markup of ours in core's header.
 */
export default function profile() {
  // Core types UserCard's attrs loosely; it is always given the user it shows.
  extend(UserCard.prototype, 'infoItems', function (items) {
    const user = this.attrs.user;
    if (!user) return;

    /** A stat reads as a number first and its label second, never as a sentence. */
    const stat = (key, value, label) => m('span.Bespoke-profileStat', {
      key
    }, [m('span.Bespoke-profileStat-value', String(value)), m('span.Bespoke-profileStat-label', label)]);
    const posts = Number(user.commentCount?.() ?? 0);
    const discussions = Number(user.discussionCount?.() ?? 0);

    /*
     * Above core's own items (lastSeen is 100, joined is 90) so the counts
     * lead and the dates follow — the order an IPS profile reads in.
     *
     * Only these two. Core already renders "Online"/last seen, and
     * fof/best-answer adds the solution count where a forum marks solutions;
     * adding our own versions would print each of them twice.
     */
    items.add('bespoke-posts', stat('posts', posts, postsLabel(posts)), 110);
    items.add('bespoke-discussions', stat('discussions', discussions, discussionsLabel(discussions)), 109);
  });
}

/*
 * Labels come from the theme's own locale so they translate, and fall back to
 * English if a translation is missing rather than rendering a raw key.
 */
function trans(key, count, fallback) {
  try {
    const out = app.translator.trans(`ernestdefoe-bespoke.forum.${key}`, {
      count
    });
    const text = Array.isArray(out) ? out.join('') : String(out ?? '');
    return text && !text.startsWith('ernestdefoe-bespoke.') ? text : fallback;
  } catch (e) {
    return fallback;
  }
}
const postsLabel = n => trans('profile_posts', n, n === 1 ? 'post' : 'posts');
const discussionsLabel = n => trans('profile_discussions', n, n === 1 ? 'discussion' : 'discussions');